import React, { useState } from 'react';
import { X, Plus, FileText, FileCheck, ClipboardList, Package, type LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

interface PageManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreatePage: (label: string, icon?: string) => void;
  existingPageNames: string[];
}

// Common icons for survey pages
const COMMON_ICONS: { name: string; Icon: LucideIcon; label: string }[] = [
  { name: 'FileText', Icon: LucideIcons.FileText, label: 'Document' },
  { name: 'FileCheck', Icon: LucideIcons.FileCheck, label: 'Checklist' },
  { name: 'ClipboardList', Icon: LucideIcons.ClipboardList, label: 'Survey' },
  { name: 'Package', Icon: LucideIcons.Package, label: 'Package' },
  { name: 'Camera', Icon: LucideIcons.Camera, label: 'Photos' },
  { name: 'MapPin', Icon: LucideIcons.MapPin, label: 'Location' },
  { name: 'Home', Icon: LucideIcons.Home, label: 'Property' },
  { name: 'Users', Icon: LucideIcons.Users, label: 'People' },
  { name: 'Wrench', Icon: LucideIcons.Wrench, label: 'Tools' },
  { name: 'HardHat', Icon: LucideIcons.HardHat, label: 'Safety' },
  { name: 'Ruler', Icon: LucideIcons.Ruler, label: 'Measurements' },
  { name: 'Zap', Icon: LucideIcons.Zap, label: 'Electrical' },
  { name: 'Droplets', Icon: LucideIcons.Droplets, label: 'Plumbing' },
  { name: 'Wind', Icon: LucideIcons.Wind, label: 'Ventilation' },
  { name: 'Thermometer', Icon: LucideIcons.Thermometer, label: 'Temperature' },
  { name: 'Shield', Icon: LucideIcons.Shield, label: 'Safety' },
];

export default function PageManagementModal({
  isOpen,
  onClose,
  onCreatePage,
  existingPageNames
}: PageManagementModalProps) {
  const [pageName, setPageName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState<string>('FileText');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    // Validate page name
    const trimmedName = pageName.trim();
    
    if (!trimmedName) {
      setError('Page name is required');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Page name must be at least 2 characters');
      return;
    }

    if (trimmedName.length > 50) {
      setError('Page name must be less than 50 characters');
      return;
    }

    // Check for duplicate names (case-insensitive)
    if (existingPageNames.some(name => name.toLowerCase() === trimmedName.toLowerCase())) {
      setError('A page with this name already exists');
      return;
    }

    // Create the page
    onCreatePage(trimmedName, selectedIcon);
    
    // Reset and close
    setPageName('');
    setSelectedIcon('FileText');
    setError('');
    onClose();
  };

  const handleClose = () => {
    setPageName('');
    setSelectedIcon('FileText');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between border-b-4 border-blue-700">
          <div className="flex items-center space-x-3">
            <Plus size={24} />
            <h2 className="text-xl font-bold">Create New Page</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-blue-700 rounded-full p-2 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Page Name Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Page Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={pageName}
              onChange={(e) => {
                setPageName(e.target.value);
                setError('');
              }}
              placeholder="e.g., Energy Assessment, Custom Inspection"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                <span className="font-medium">⚠</span>
                <span>{error}</span>
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              This name will appear in the navigation menu
            </p>
          </div>

          {/* Icon Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Choose an Icon (Optional)
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {COMMON_ICONS.map(({ name, Icon, label }) => {
                const isSelected = selectedIcon === name;
                return (
                  <button
                    key={name}
                    onClick={() => setSelectedIcon(name)}
                    className={`
                      flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all
                      ${isSelected
                        ? 'bg-blue-50 border-blue-500 shadow-md scale-105'
                        : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }
                    `}
                    title={label}
                  >
                    <Icon size={24} className={isSelected ? 'text-blue-600' : 'text-gray-600'} />
                    <span className="text-[10px] mt-1 text-gray-600 truncate max-w-full">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info Message */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <FileText size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">What happens next?</p>
                <p className="text-xs text-blue-700 mt-1">
                  After creating this page, you can add custom fields by clicking the "Edit Form" button.
                  The page will appear in your navigation menu and you can reorder it by dragging.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl flex items-center justify-end space-x-3 border-t-2 border-gray-200">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 rounded-xl font-medium text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!pageName.trim()}
            className={`
              px-6 py-2.5 rounded-xl font-medium text-white transition-all
              ${pageName.trim()
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg'
                : 'bg-gray-300 cursor-not-allowed'
              }
            `}
          >
            <span className="flex items-center space-x-2">
              <Plus size={18} />
              <span>Create Page</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
